import { TextObjectKinds, IObjectNameIdentifier } from "../../../../../../documaker/domain/format/objects";
import { ITextObject } from "../../../../../domain/editableFormat/objects";
import { ITextObjectBean } from "../../beans/objects";
import { AbstractObjectTranslator } from "./AbstractObjectTranslator";
import {
  EditBehaviorTranslator,
  PageExtendBehaviorTranslator,
  InputMethodBehaviorTranslator,
  TextObjectKindTranslator,
  TurnedTextStyleTranslator,
  NumberStyleTranslator,
  NumberRangeAlertBehaviorTranslator,
  FocusBehaviorTranslator,
  PaintStyleOnEditingTranslator,
  ItemsStyleTranslator,
  ItemGroupSelectBehaviorTranslator,
  HisInformationItemDefaultSelectBehaviorTranslator,
  HospitalizedHisInformationItemSelectBehaviorTranslator,
  UserDefinedInformationSourceTranslator,
  UserDefinedItemSelectBehaviorTranslator,
  DesignatedDoctorInformationSourceTranslator,
  CopyLinkedTextsBehaviorTranslator,
  QuestionnaireTranslator,
  RelationReplacer,
} from "./properties";
import { StandardTextDrawableObjectTranslator } from "./base";

export class TextObjectTranslator extends AbstractObjectTranslator<ITextObject, ITextObjectBean> {
  constructor() {
    super(new StandardTextDrawableObjectTranslator());
  }

  protected shouldRecordName(source: ITextObjectBean): boolean {
    return (source["@name"] !== "指定医番号") && !(/graph_[0-9]+\[[1-4]\]/.test(source["@name"]));
  }

  protected readName(source: ITextObjectBean): IObjectNameIdentifier {
    return source["@name"]
      ? {
        key: source["@name"],
      }
      : {
        name: source["@title"],
      };
  }

  overwriteObject(source: ITextObject, bean: ITextObjectBean, formatId: number): void {
    source.objectKind = this.objectKind.from(bean);
    if (this.isInformation(source.objectKind)) {
      source.name = bean["@name-sub"];
      source.informationKey = bean["@name"];
    } else {
      source.name = bean["@title"];
      source.informationKey = "";
    }
    source.editBehavior = this.editBehavior.from(bean);
    source.pageExtendBehavior = this.pageExtendBehavior.from(bean);
    source.inputMethodBehavior = this.inputMethodBehavior.from(bean);
    source.turnedTextStyle = this.turnedTextStyle.from(bean);
    source.numberStyle = this.numberStyle.from(bean);
    source.numberRangeAlertBehavior = this.numberRangeAlertBehavior.from(bean);
    source.focusBehavior = this.focusBehavior.from(bean);
    source.paintStyleOnEditing = this.paintStyleOnEditing.from(bean);
    source.itemsStyle = this.itemsStyle.from(bean);
    source.itemGroupSelectBehavior = this.itemGroupSelectBehavior.from(bean);
    source.hisInformationItemDefaultSelectBehavior = this.hisInformationItemDefaultSelectBehavior.from(bean);
    source.hospitalizedHisInformationItemSelectBehavior = this.hospitalizedHisInformationItemSelectBehavior.from(bean);
    source.userDefinedInformationSource = this.userDefinedInformationSource.from(bean);
    source.userDefinedItemSelectBehavior = this.userDefinedItemSelectBehavior.from(bean);
    source.designatedDoctorInformationSource = this.designatedDoctorInformationSource.from(bean);
    source.copyLinkedTextsBehavior = this.copyLinkedTextsBehavior.from(bean);
    source.questionnaire = this.questionnaire.from(bean);
  }

  private isInformation(kind: TextObjectKinds): boolean {
    return (kind === TextObjectKinds.HisItem)
        || (kind === TextObjectKinds.UserDefined)
        || (kind === TextObjectKinds.DesignatedDoctorNumber)
        || (kind === TextObjectKinds.RangeOfMotion);
  }

  overwriteBean(source: ITextObjectBean, object: ITextObject): void {
    if (this.isInformation(object.objectKind)) {
      source["@name"] = object.informationKey;
      source["@title"] = undefined;
      source["@name-sub"] = object.name;
    } else {
      source["@name"] = "";
      source["@title"] = object.name;
      source["@name-sub"] = undefined;
    }
    source["@vanish"] = this.editBehavior.to(object).vanish;
    const pageExtendBehavior = this.pageExtendBehavior.to(object);
    source["@auto-notepage-size"] = pageExtendBehavior.autoNotepageSize;
    source["@auto-notepage-type"] = pageExtendBehavior.autoNotepageType;
    source["@auto-notepage-overwrite"] = pageExtendBehavior.autoNotepageOverwrite;
    source["@input-code"] = this.inputMethodBehavior.to(object).inputCode;
    source["@asterisk"] = this.turnedTextStyle.to(object).asterisk;
    source["@text-format"] = this.numberStyle.to(object).textFormat;
    const numberRangeAlertBehavior = this.numberRangeAlertBehavior.to(object);
    source["@high-value"] = numberRangeAlertBehavior.highValue;
    source["@high-over-color"] = numberRangeAlertBehavior.highOverColor;
    source["@low-value"] = numberRangeAlertBehavior.lowValue;
    source["@low-over-color"] = numberRangeAlertBehavior.lowOverColor;
    source["@focus"] = this.focusBehavior.to(object).focus;
    source["@view-bg"] = this.paintStyleOnEditing.to(object).viewBg;
    source["@separate-word"] = this.itemsStyle.to(object).separateWord;
    source["@group"] = this.itemGroupSelectBehavior.to(object).group;
    if (object.objectKind === TextObjectKinds.Fixed) {                  // 固定文字列のみ.(それ以外は前値保持.)
      this.relationReplacer.replaceBeans(
        source,
        "copies-text",
        this.copyLinkedTextsBehavior.to(object));
    }
    if (object.objectKind === TextObjectKinds.HisItem) {                // HIS情報のみ.(それ以外は前値保持.)
      source["@initial"] = this.hisInformationItemDefaultSelectBehavior.to(object).initial;
      const hospitalizedHisInformationItemSelectBehavior = this.hospitalizedHisInformationItemSelectBehavior.to(object);
      source["@admission"] = object.hisInformationItemDefaultSelectBehavior.isEnable
        ? hospitalizedHisInformationItemSelectBehavior.admission
        : undefined;
      this.relationReplacer.replaceBeans(
        source,
        "base-date",
        object.hospitalizedHisInformationItemSelectBehavior.baseDateReferBehavior.isEnable
          ? hospitalizedHisInformationItemSelectBehavior.relationalBaseDate
          : undefined);
    }
    if (object.objectKind === TextObjectKinds.UserDefined) {            // ユーザ定義情報のみ.(それ以外は前値保持.)
      source["@source-file"] = this.userDefinedInformationSource.to(object).sourceFile;
      const userDefinedItemSelectBehavior = this.userDefinedItemSelectBehavior.to(object);
      source["@select-mode"] = userDefinedItemSelectBehavior.selectMode;
      this.relationReplacer.replaceBeans(
        source,
        "window-type",
        userDefinedItemSelectBehavior.relationalWindowType);
    }
    if (object.objectKind === TextObjectKinds.DesignatedDoctorNumber) { // 指定医番号情報のみ.(それ以外は前値保持.)
      this.relationReplacer.replaceBeans(
        source,
        "designate-doctor",
        this.designatedDoctorInformationSource.to(object).relationalDesignateDoctor);
    }
    this.relationReplacer.replaceBeans(source, "questionnaire", this.questionnaire.to(object));
    this.relationReplacer.replaceBeans(source, "questionnaire-children", this.questionnaire.toChildren(object));
  }

  private readonly editBehavior = new EditBehaviorTranslator();
  private readonly pageExtendBehavior = new PageExtendBehaviorTranslator();
  private readonly objectKind = new TextObjectKindTranslator();
  private readonly inputMethodBehavior = new InputMethodBehaviorTranslator();
  private readonly turnedTextStyle = new TurnedTextStyleTranslator();
  private readonly numberStyle = new NumberStyleTranslator();
  private readonly numberRangeAlertBehavior = new NumberRangeAlertBehaviorTranslator();
  private readonly focusBehavior = new FocusBehaviorTranslator();
  private readonly paintStyleOnEditing = new PaintStyleOnEditingTranslator();
  private readonly itemsStyle = new ItemsStyleTranslator();
  private readonly itemGroupSelectBehavior = new ItemGroupSelectBehaviorTranslator();
  private readonly hisInformationItemDefaultSelectBehavior = new HisInformationItemDefaultSelectBehaviorTranslator();
  private readonly hospitalizedHisInformationItemSelectBehavior = new HospitalizedHisInformationItemSelectBehaviorTranslator();
  private readonly userDefinedInformationSource = new UserDefinedInformationSourceTranslator();
  private readonly userDefinedItemSelectBehavior = new UserDefinedItemSelectBehaviorTranslator();
  private readonly designatedDoctorInformationSource = new DesignatedDoctorInformationSourceTranslator();
  private readonly copyLinkedTextsBehavior = new CopyLinkedTextsBehaviorTranslator();
  private readonly questionnaire = new QuestionnaireTranslator();
  private readonly relationReplacer = new RelationReplacer();
}
